import { Box, Grid } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import MetricsGrid from './MetricsGrid';
import ChartCard from './ChartCard';
import ExportButton from './ExportButton';
import { AdminAnalyticsData } from '../../../types/admin';
import { formatCurrency, formatPercentage } from '../../../utils/formatters';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface RevenueTabProps {
  data: AdminAnalyticsData;
  loading?: boolean;
  onExport: () => void;
}

/**
 * RevenueTab - Revenue analytics and financial metrics
 *
 * Displays:
 * - Revenue KPIs (Total Revenue, MRR, ARR, ARPU, LTV, CAC)
 * - MRR growth chart with trend line
 * - Subscription tier distribution over time (stacked area)
 * - Revenue breakdown by tier (pie chart)
 */
export default function RevenueTab({ data, loading, onExport }: RevenueTabProps) {
  // Prepare metrics for cards
  const metrics = [
    {
      title: 'Total Revenue',
      value: formatCurrency(data.revenue.total_revenue, true),
      subtitle: 'Lifetime revenue',
      icon: <AttachMoneyIcon />,
      color: '#1976d2',
      trend: {
        value: 12.5,
        label: 'vs last year',
        direction: 'up' as const,
      },
    },
    {
      title: 'Monthly Recurring Revenue',
      value: formatCurrency(data.revenue.mrr, true),
      subtitle: 'Current MRR',
      icon: <TrendingUpIcon />,
      color: '#2e7d32',
      trend: {
        value: 8.1,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Annual Recurring Revenue',
      value: formatCurrency(data.revenue.arr, true),
      subtitle: 'Current ARR',
      icon: <AccountBalanceIcon />,
      color: '#9c27b0',
      trend: {
        value: 8.1,
        label: 'vs last year',
        direction: 'up' as const,
      },
    },
    {
      title: 'Average Revenue Per User',
      value: formatCurrency(data.revenue.arpu),
      subtitle: 'Per paying customer',
      icon: <PersonIcon />,
      color: '#ed6c02',
      trend: {
        value: 3.2,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Customer Lifetime Value',
      value: formatCurrency(data.revenue.ltv),
      subtitle: 'Average LTV',
      icon: <TrendingUpIcon />,
      color: '#0288d1',
      trend: {
        value: 5.8,
        label: 'vs last quarter',
        direction: 'up' as const,
      },
    },
    {
      title: 'Customer Acquisition Cost',
      value: formatCurrency(data.revenue.cac),
      subtitle: 'Average CAC',
      icon: <AttachMoneyIcon />,
      color: '#d32f2f',
      trend: {
        value: -2.3,
        label: 'vs last quarter',
        direction: 'down' as const,
      },
    },
    {
      title: 'LTV:CAC Ratio',
      value: (data.revenue.ltv / data.revenue.cac).toFixed(2) + ':1',
      subtitle: 'Efficiency metric',
      icon: <TrendingUpIcon />,
      color: '#2e7d32',
      trend: {
        value: 8.5,
        label: 'vs last quarter',
        direction: 'up' as const,
      },
    },
    {
      title: 'Refund Rate',
      value: formatPercentage(data.revenue.refund_rate, 1),
      subtitle: 'Monthly refund rate',
      icon: <AttachMoneyIcon />,
      color: '#f57c00',
      trend: {
        value: -0.2,
        label: 'vs last month',
        direction: 'down' as const,
      },
    },
  ];

  // Prepare MRR growth data (last 90 days)
  const revenueHistoryDates = data.revenueHistory.map((d) => new Date(d.date));
  const mrrData = data.revenueHistory.map((d) => d.mrr);
  const newRevenueData = data.revenueHistory.map((d) => d.new_revenue);
  const churnedRevenueData = data.revenueHistory.map((d) => d.churned_revenue);

  // Prepare subscription tier distribution data (last 30 days for clarity)
  const last30DaysSubscriptions = data.subscriptionTiers.slice(-30);
  const subscriptionDates = last30DaysSubscriptions.map((d) => new Date(d.date));
  const freeTierData = last30DaysSubscriptions.map((d) => d.free);
  const proTierData = last30DaysSubscriptions.map((d) => d.pro);
  const agentTierData = last30DaysSubscriptions.map((d) => d.agent);

  // Current tier distribution for pie chart
  const latestTiers = data.subscriptionTiers[data.subscriptionTiers.length - 1];

  // Calculate revenue by tier (estimated)
  const proMonthlyRevenue = latestTiers.pro * 10; // $10/month
  const agentMonthlyRevenue = latestTiers.agent * 25; // $25/month
  const revenueByTier = [
    { id: 0, value: proMonthlyRevenue, label: 'Pro Tier' },
    { id: 1, value: agentMonthlyRevenue, label: 'Agent Tier' },
  ];

  return (
    <Box>
      {/* Top row: Revenue KPIs */}
      <Box sx={{ mb: 4 }}>
        <MetricsGrid metrics={metrics} loading={loading} />
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* MRR Growth Chart */}
        <Grid item xs={12}>
          <ChartCard
            title="MRR Growth Trend"
            subtitle="Monthly recurring revenue, new revenue, and churn over last 90 days"
            loading={loading}
            action={<ExportButton onExport={onExport} />}
            height={400}
          >
            <LineChart
              xAxis={[
                {
                  data: revenueHistoryDates,
                  scaleType: 'time',
                  valueFormatter: (date) =>
                    new Date(date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    }),
                },
              ]}
              yAxis={[
                {
                  valueFormatter: (value) => formatCurrency(value, true),
                },
              ]}
              series={[
                {
                  data: mrrData,
                  label: 'MRR',
                  color: '#2e7d32',
                  showMark: false,
                  area: true,
                },
                {
                  data: newRevenueData,
                  label: 'New Revenue',
                  color: '#1976d2',
                  showMark: false,
                },
                {
                  data: churnedRevenueData,
                  label: 'Churned Revenue',
                  color: '#d32f2f',
                  showMark: false,
                },
              ]}
              grid={{ vertical: true, horizontal: true }}
              sx={{
                '& .MuiLineElement-root': {
                  strokeWidth: 2,
                },
              }}
            />
          </ChartCard>
        </Grid>

        {/* Subscription Tier Growth */}
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Subscription Tier Growth"
            subtitle="User growth across subscription tiers over last 30 days"
            loading={loading}
            height={400}
          >
            <LineChart
              xAxis={[
                {
                  data: subscriptionDates,
                  scaleType: 'time',
                  valueFormatter: (date) =>
                    new Date(date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    }),
                },
              ]}
              series={[
                {
                  data: freeTierData,
                  label: 'Free',
                  color: '#9e9e9e',
                  showMark: false,
                  area: true,
                  stack: 'total',
                },
                {
                  data: proTierData,
                  label: 'Pro',
                  color: '#1976d2',
                  showMark: false,
                  area: true,
                  stack: 'total',
                },
                {
                  data: agentTierData,
                  label: 'Agent',
                  color: '#2e7d32',
                  showMark: false,
                  area: true,
                  stack: 'total',
                },
              ]}
              grid={{ vertical: true, horizontal: true }}
            />
          </ChartCard>
        </Grid>

        {/* Revenue by Tier Pie Chart */}
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Revenue by Tier"
            subtitle="Current MRR distribution"
            loading={loading}
            height={400}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <PieChart
                series={[
                  {
                    data: revenueByTier,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    valueFormatter: (item) => formatCurrency(item.value, true),
                  },
                ]}
                height={300}
                slotProps={{
                  legend: {
                    direction: 'column',
                    position: { vertical: 'bottom', horizontal: 'middle' },
                    padding: 0,
                  },
                }}
              />
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
